import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Container, TextField, Button, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from './api';
import Header from './components/Header';
import { useConfirmBackLogout } from './common/useConfirmBackLogout';
import BackButton from './common/BackButton';
import { useNavigationHistory } from "./common/navigationHistory.js";
import { validateSelectedFiles } from "./common/fileUploadValidation.js";

const allowedAttachmentTypes = '.pdf,.txt,.xlsx,.pptx,.docx';

const CreateMail = () => {
  const [eventDate, setEventDate] = useState('');
  const [bulkMailTitle, setBulkMailTitle] = useState('');
  const [bulkMailBody, setBulkMailBody] = useState('');
  const [notificationExhibitors, setNotificationExhibitors] = useState([]);
  const [history, setHistory] = useState([]);
  const [showFormMenu, setShowFormMenu] = useState(false);
  const navigate = useNavigate();
  const [selectedExhibitors, setSelectedExhibitors] = useState([]);
  const [mailTarget, setMailTarget] = useState({ type: "notification", company: null, openedBy: "default" });
  const [mailSubject, setMailSubject] = useState("");
  const [noticeMailBody, setNoticeMailBody] = useState("");
  const [noticeMailFiles, setNoticeMailFiles] = useState([]);
  const [filteredExhibitors, setFilteredExhibitors] = useState([]);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [recipientPage, setRecipientPage] = useState(1);
  const [remindMailFiles, setRemindMailFiles] = useState([]);
  const [mailEditorType, setMailEditorType] = useState("notification");
  const [recipientBoothSearch, setRecipientBoothSearch] = useState("");
  const [recipientCompanySearch, setRecipientCompanySearch] = useState("");
  const recipientsPerPage = 10;

  const fetchNotificationExhibitors = async () => {
    try {
      const res = await api.get('/api/exhibitors');
      setNotificationExhibitors(res.data);
      setFilteredExhibitors(res.data);
    } catch (err) {
      console.error('出展者一覧の取得に失敗:', err);
    }
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      const templateType = mailEditorType === 'notification' ? 'notification' : 'remind';

      try {
        const templateRes = await api.get(`/api/mail-templates/${templateType}`);
        const template = templateRes.data;

        if (mailEditorType === 'notification') {
          setMailSubject(template.subject || '');
          setNoticeMailBody(template.body || '');
        } else {
          setBulkMailTitle(template.subject || '');
          setBulkMailBody(template.body || '');
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error(`${templateType}メールテンプレート取得失敗:`, err);
        }
      }
    };

    fetchTemplate();
  }, [mailEditorType]);

  const handleNotificationSend = async () => {
    const count = selectedExhibitors.length;
    if (count === 0) {
      alert('送信先が選択されていません');
      return;
    }
    if (!mailSubject || !noticeMailBody) {
      alert('件名と本文を入力してください');
      return;
    }

    const ok = window.confirm(`${count}件に通知メールを送信します。よろしいですか？`);
    if (!ok) return;

    try {
      const formData = new FormData();
      formData.append('subject', mailSubject);
      formData.append('body', noticeMailBody);
      formData.append('exhibitor_ids', JSON.stringify(selectedExhibitors));
      noticeMailFiles.forEach((file) => {
        formData.append('files', file);
      });

      const res = await api.post('/api/send-notification', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.status === 200) {
        alert('通知メールを送信しました');
      }
    } catch (err) {
      console.error(err);
      alert('通知メール送信に失敗しました');
    }
  };

  const handleNotificationTestSend = async () => {
    if (!mailSubject || !noticeMailBody) {
      alert('件名と本文を入力してください');
      return;
    }

    const ok = window.confirm('テストメールを送信します。よろしいですか？');
    if (!ok) return;

    try {
      const formData = new FormData();
      formData.append('subject', mailSubject);
      formData.append('body', noticeMailBody);
      noticeMailFiles.forEach((file) => {
        formData.append('files', file);
      });

      const res = await api.post('/api/send-notification-test', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.status === 200) {
        alert('テストメールを送信しました');
      }
    } catch (err) {
      console.error(err);
      alert('テストメール送信に失敗しました');
    }
  };

  const handleCheckboxChange = (ex) => {
    setSelectedExhibitors((prev) => {
      let newSelected;
      if (prev.includes(ex.exhibitor_id)) {
        newSelected = prev.filter((id) => id !== ex.exhibitor_id);
      } else {
        newSelected = [...prev, ex.exhibitor_id];
      }

      if (newSelected.length > 0) {
        const firstSelectedCompany = filteredExhibitors.find(
          (e) => e.exhibitor_id === newSelected[0]
        );
        setMailTarget({
          type: 'notification',
          company: firstSelectedCompany,
          openedBy: 'checkbox',
        });
      } else {
        setMailTarget({ type: 'notification', company: null, openedBy: 'default' });
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (filteredExhibitors.length === 0) {
      return;
    }

    if (selectedExhibitors.length === recipientFilteredExhibitors.length) {
      setSelectedExhibitors([]);
      setMailTarget({ type: 'notification', company: null, openedBy: 'default' });
    } else {
      const allIds = recipientFilteredExhibitors.map((ex) => ex.exhibitor_id);
      setSelectedExhibitors(allIds);
      setMailTarget({
        type: 'notification',
        company: recipientFilteredExhibitors[0],
        openedBy: 'checkbox',
      });
    }
  };

  useEffect(() => {
    fetchNotificationExhibitors();
  }, []);

  useEffect(() => {
    api.get('/api/mail-history')
      .then(res => setHistory(res.data))
      .catch(err => console.error('履歴取得失敗:', err));
  }, []);

  useEffect(() => {
    api.get('/api/mail-settings/get')
      .then((res) => {
        const { event_date } = res.data;
        setEventDate(event_date ?? '');
      })
      .catch(err => {
        console.error('メール設定取得失敗:', err);
      });
  }, []);

  const buildRemindFormData = ({ includeEventDate = false } = {}) => {
    const formData = new FormData();
    formData.append('mail_subject', bulkMailTitle);
    formData.append('mail_body', bulkMailBody);
    if (includeEventDate) {
      formData.append('event_date', eventDate);
    }
    remindMailFiles.forEach((file) => {
      formData.append('files', file);
    });
    return formData;
  };

  const handleBulkSend = async () => {
    if (!bulkMailTitle || !bulkMailBody) {
      alert('件名と本文を入力してください');
      return;
    }

    const ok = window.confirm('全申請書再提出・未提出向けの催促メールを手動送信します。よろしいですか？');
    if (!ok) return;

    try {
      const res = await api.post('/api/mail/send-remind', buildRemindFormData(), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert(res.data.message || '催促メール送信ジョブを登録しました');
    } catch (err) {
      console.error(err);
      alert('送信に失敗しました');
    }
  };

  const handleRequiredBulkSend = async () => {
    if (!bulkMailTitle || !bulkMailBody) {
      alert('件名と本文を入力してください');
      return;
    }

    const ok = window.confirm('必須書類未提出向けの催促メールを手動送信します。よろしいですか？');
    if (!ok) return;

    try {
      const res = await api.post('/api/mail/send-required-remind', buildRemindFormData(), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert(res.data.message || '必須書類未提出向け送信ジョブを登録しました');
    } catch (err) {
      console.error(err);
      alert('送信に失敗しました');
    }
  };

  const handleScheduleBulkSend = async () => {
    if (!bulkMailTitle || !bulkMailBody) {
      alert('件名と本文を入力してください');
      return;
    }
    if (!eventDate) {
      alert('予約送信日時を入力してください');
      return;
    }

    const ok = window.confirm('全申請書再提出・未提出向けの催促メールを予約送信します。よろしいですか？');
    if (!ok) return;

    try {
      const res = await api.post('/api/mail/schedule-remind', buildRemindFormData({ includeEventDate: true }), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert(res.data.message || '予約送信ジョブを登録しました');
    } catch (err) {
      console.error(err);
      alert('予約送信に失敗しました');
    }
  };

  const handleScheduleRequiredBulkSend = async () => {
    if (!bulkMailTitle || !bulkMailBody) {
      alert('件名と本文を入力してください');
      return;
    }
    if (!eventDate) {
      alert('予約送信日時を入力してください');
      return;
    }

    const ok = window.confirm('必須書類未提出向けの催促メールを予約送信します。よろしいですか？');
    if (!ok) return;

    try {
      const res = await api.post('/api/mail/schedule-required-remind', buildRemindFormData({ includeEventDate: true }), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert(res.data.message || '必須書類未提出向け予約送信ジョブを登録しました');
    } catch (err) {
      console.error(err);
      alert('予約送信に失敗しました');
    }
  };

  const handleRemindTestSend = async () => {
    if (!bulkMailTitle || !bulkMailBody) {
      alert('件名と本文を入力してください');
      return;
    }

    const ok = window.confirm('テストメールを送信します。送信先は固定のテストアドレスです。');
    if (!ok) return;

    try {
      const res = await api.post('/api/mail/send-to-unsubmitted-test', buildRemindFormData(), {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('テストメールを送信しました');
    } catch (err) {
      console.error(err);
      alert('テスト送信に失敗しました');
    }
  };

  const handleDownloadLatestLog = async () => {
    try {
      const isNotification = mailEditorType === "notification";
      const res = await api.get('/api/mail/latest-log', {
        params: { job_type: isNotification ? 'notification' : 'remind' },
        responseType: 'blob'
      });

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isNotification
        ? '通知メール_最新メール送信ログ.xlsx'
        : '催促メール_最新メール送信ログ.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('最新送信ログのダウンロードに失敗しました');
    }
  };

  const aggregatedHistory = history.reduce((acc, row) => {
    const utcDate = new Date(row.sent_at);
    const jstDate = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000);

    const dateStr = jstDate.getFullYear() + '/' +
                    String(jstDate.getMonth() + 1).padStart(2, '0') + '/' +
                    String(jstDate.getDate()).padStart(2, '0') + ' ' +
                    String(jstDate.getHours()).padStart(2, '0') + ':' +
                    String(jstDate.getMinutes()).padStart(2, '0') + ':' +
                    String(jstDate.getSeconds()).padStart(2, '0');

    const existing = acc.find(item => item.dateStr === dateStr);
    if (existing) {
      existing.recipientsCount += row.recipients.length;
    } else {
      acc.push({ dateStr, recipientsCount: row.recipients.length });
    }
    return acc;
  }, []);

  const selectedCompanyNames = useMemo(() => {
    if (selectedExhibitors.length === 0) {
      return [];
    }

    return notificationExhibitors
      .filter((ex) => selectedExhibitors.includes(ex.exhibitor_id))
      .map((ex) => ex.company_name);
  }, [notificationExhibitors, selectedExhibitors]);

  const recipientFilteredExhibitors = filteredExhibitors.filter((ex) => {
    const boothMatch = (ex.booth_number || "").includes(recipientBoothSearch);
    const companyMatch = (ex.company_name || "").includes(recipientCompanySearch);
    return boothMatch && companyMatch;
  });

  const totalRecipientPages = Math.max(1, Math.ceil(recipientFilteredExhibitors.length / recipientsPerPage));
  const paginatedRecipients = recipientFilteredExhibitors.slice(
    (recipientPage - 1) * recipientsPerPage,
    recipientPage * recipientsPerPage
  );

  useEffect(() => {
    setRecipientPage(1);
  }, [recipientBoothSearch, recipientCompanySearch]);

  useConfirmBackLogout(navigate);
  useNavigationHistory();

  return (
    <div>
      <Header
        title="SDGs子ども・ユースフェア メール作成"
      />
      <Container sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: '20%', pr: 2, borderRight: '1px solid #ccc' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box className="menu-item" onClick={() => navigate('/host-dashboard')}>
                申請確認画面
              </Box>
              <Box
                className="menu-group"
                id="hover-block"
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              >
                <Box className="menu-item reverse-triangle" onClick={() => setShowFormMenu(prev => !prev)}>
                  会社情報
                </Box>
                {showFormMenu && (
                  <Box className="hover-menu">
                    <Typography
                      sx={{ color: '#0071bc', textAlign: 'center' }}
                      className='form-button'
                      onClick={() => navigate('/exhibitor-info')}
                    >
                      出展者
                    </Typography>
                    <Typography
                      sx={{ color: '#0071bc', textAlign: 'center' }}
                      className='form-button'
                      onClick={() => navigate('/corporate-info')}
                    >
                      協力会社
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box className="menu-item" onClick={() => navigate('/create-mail')}>
                メール作成
              </Box>
              <Box className="menu-item" onClick={() => navigate('/create-notice')}>
                お知らせ作成
              </Box>
              <Box className="menu-item" onClick={() => navigate('/form-history')}>
                提出履歴確認
              </Box>
            </Box>
            <BackButton />
          </Box>

          <Box sx={{ width: '80%', pl: 2 }}>
            <Box sx={{ pb: 5 }}>
              <Typography variant="h6" className="title" gutterBottom sx={{ mb: 2 }}>
                メール作成
              </Typography>

              <FormControl sx={{ minWidth: 260, mb: 3, mt: 2 }}>
                <InputLabel id="mail-editor-type-label">メール種別</InputLabel>
                <Select
                  labelId="mail-editor-type-label"
                  value={mailEditorType}
                  label="メール種別"
                  onChange={(e) => setMailEditorType(e.target.value)}
                >
                  <MenuItem value="notification">通知メール</MenuItem>
                  <MenuItem value="remind">催促メール</MenuItem>
                </Select>
              </FormControl>

              {mailEditorType === 'notification' ? (
                <>
                  <Typography gutterBottom sx={{ mb: 2 }}>
                    任意の出展者に通知メールを送信します。送信先は「送信先選択」から選択してください。
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setRecipientPage(1);
                        setIsRecipientModalOpen(true);
                      }}
                    >
                      送信先選択
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleNotificationTestSend}
                    >
                      テストメール送信
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDownloadLatestLog}
                    >
                      最新送信ログをダウンロード
                    </Button>
                  </Box>

                  <Typography sx={{ mb: 2 }}>
                    選択中: {selectedExhibitors.length}件
                  </Typography>

                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      backgroundColor: '#fafafa',
                      minHeight: 72,
                    }}
                  >
                    {selectedCompanyNames.length > 0 ? (
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedCompanyNames.join('、')}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">
                        送信先がまだ選択されていません。
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    label="件名"
                    fullWidth
                    value={mailSubject}
                    onChange={(e) => setMailSubject(e.target.value)}
                    sx={{ my: 2 }}
                  />

                  <TextField
                    label="本文"
                    multiline
                    rows={6}
                    fullWidth
                    value={noticeMailBody}
                    onChange={(e) => setNoticeMailBody(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Box sx={{ mb: 3 }}>
                    <input
                      type="file"
                      multiple
                      accept={allowedAttachmentTypes}
                      onChange={(e) => {
                        const validation = validateSelectedFiles(e.target.files);
                        if (!validation.valid) {
                          alert(validation.message);
                          e.target.value = null;
                          return;
                        }
                        setNoticeMailFiles(validation.files);
                      }}
                    />
                  </Box>

                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNotificationSend}
                    >
                      送信
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography gutterBottom sx={{ mb: 2, color: 'red' }}>
                    手動送信と予約送信で同じ件名・本文・添付ファイルを使用します。予約送信時のみ送信日時を入力してください。
                  </Typography>

                  <TextField
                    label="予約送信日時"
                    type="datetime-local"
                    fullWidth
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    sx={{ my: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    label="件名"
                    fullWidth
                    value={bulkMailTitle}
                    onChange={(e) => setBulkMailTitle(e.target.value)}
                    sx={{ my: 2 }}
                  />

                  <TextField
                    label="本文"
                    multiline
                    rows={6}
                    fullWidth
                    value={bulkMailBody}
                    onChange={(e) => setBulkMailBody(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Box sx={{ mb: 3 }}>
                    <input
                      type="file"
                      multiple
                      accept={allowedAttachmentTypes}
                      onChange={(e) => {
                        const validation = validateSelectedFiles(e.target.files);
                        if (!validation.valid) {
                          alert(validation.message);
                          e.target.value = null;
                          return;
                        }
                        setRemindMailFiles(validation.files);
                      }}
                    />
                  </Box>

                  <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleBulkSend}
                    >
                      手動 全申請書再提出・未提出向け送信
                    </Button>

                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleRequiredBulkSend}
                    >
                      手動 必須書類未提出向け送信
                    </Button>

                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleRemindTestSend}
                    >
                      テストメール送信
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleScheduleBulkSend}
                    >
                      予約 全申請書再提出・未提出向け送信
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleScheduleRequiredBulkSend}
                    >
                      予約 必須書類未提出向け送信
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDownloadLatestLog}
                    >
                      送信ログのダウンロード
                    </Button>
                  </Box>
                </>
              )}
            </Box>

            {/* <Box sx={{ mt: 6 }}>
              <Typography variant="h6" gutterBottom>送信履歴</Typography>
              <TableContainer component={Box}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>送信日時</TableCell>
                      <TableCell>宛先数</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aggregatedHistory.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.dateStr}</TableCell>
                        <TableCell>{row.recipientsCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box> */}
          </Box>
        </Box>
      </Container>

      <Dialog
        open={isRecipientModalOpen}
        onClose={() => setIsRecipientModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>送信先選択</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Typography>
              選択中: {selectedExhibitors.length}件
            </Typography>
            <Button variant="outlined" onClick={handleSelectAll}>
              {selectedExhibitors.length === recipientFilteredExhibitors.length ? '全選択解除' : '全選択'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="小間番号検索"
              size="small"
              value={recipientBoothSearch}
              onChange={(e) => setRecipientBoothSearch(e.target.value)}
            />
            <TextField
              label="会社名検索"
              size="small"
              value={recipientCompanySearch}
              onChange={(e) => setRecipientCompanySearch(e.target.value)}
            />
          </Box>

          <TableContainer component={Box}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">選択</TableCell>
                  <TableCell>小間番号</TableCell>
                  <TableCell>会社名</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecipients.map((ex) => (
                  <TableRow key={ex.exhibitor_id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedExhibitors.includes(ex.exhibitor_id)}
                        onChange={() => handleCheckboxChange(ex)}
                      />
                    </TableCell>
                    <TableCell>{ex.booth_number || '-'}</TableCell>
                    <TableCell>{ex.company_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              disabled={recipientPage === 1}
              onClick={() => setRecipientPage((prev) => prev - 1)}
            >
              前へ
            </Button>
            <Typography>
              {recipientPage} / {totalRecipientPages}
            </Typography>
            <Button
              variant="outlined"
              disabled={recipientPage === totalRecipientPages}
              onClick={() => setRecipientPage((prev) => prev + 1)}
            >
              次へ
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRecipientModalOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <footer>
        <Typography>
          2026 THE MID-JAPAN ECONOMIST.
        </Typography>
      </footer>
    </div>
  );
};

export default CreateMail;
